import { apiClient } from './apiClient';

export type SalesProductLinkedApplication = {
  applicationId: string;
  name: string;
};

export type SalesProductAzureLink = {
  id: string;
  salesProductId: string;
  backlogItemId?: string | null;
  changeRequestId?: string | null;
  applicationId: string;
  applicationName?: string | null;
  kind: string;
  externalId: string;
  title: string;
  state?: string | null;
  url?: string | null;
  syncedAtUtc: string;
};

export type SalesProductAzureLinkResolvePayload = {
  applicationId: string;
  input: string;
  kind?: string;
};

export type SalesProductBacklogItem = {
  id: string;
  salesProductId: string;
  title: string;
  description?: string | null;
  priority: string;
  status: string;
  plannedDeliveryDateUtc?: string | null;
  estimatePoints?: number | null;
  estimateHours?: number | null;
  sortOrder: number;
  assigneeUserId?: string | null;
  assigneeUserName?: string | null;
  tags?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  azureLinks: SalesProductAzureLink[];
};

export type SalesProductBacklogItemUpsertPayload = {
  title: string;
  description?: string | null;
  priority: string;
  status: string;
  plannedDeliveryDateUtc?: string | null;
  estimatePoints?: number | null;
  estimateHours?: number | null;
  sortOrder: number;
  assigneeUserId?: string | null;
  tags?: string | null;
};

export type SalesProductRoadmapItem = {
  id: string;
  salesProductId: string;
  title: string;
  description?: string | null;
  kind: string;
  status: string;
  plannedDateUtc?: string | null;
  actualDateUtc?: string | null;
  sortOrder: number;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type SalesProductRoadmapItemUpsertPayload = {
  title: string;
  description?: string | null;
  kind: string;
  status: string;
  plannedDateUtc?: string | null;
  actualDateUtc?: string | null;
  sortOrder: number;
};

export type SalesProductRisk = {
  id: string;
  salesProductId: string;
  title: string;
  description?: string | null;
  probability: string;
  impact: string;
  treatmentPriority: number;
  mitigationPlan?: string | null;
  contingencyPlan?: string | null;
  status: string;
  ownerUserId?: string | null;
  ownerUserName?: string | null;
  identifiedAtUtc: string;
  closedAtUtc?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type SalesProductRiskUpsertPayload = {
  title: string;
  description?: string | null;
  probability: string;
  impact: string;
  treatmentPriority: number;
  mitigationPlan?: string | null;
  contingencyPlan?: string | null;
  status: string;
  ownerUserId?: string | null;
  identifiedAtUtc: string;
  closedAtUtc?: string | null;
};

export type SalesProductIssue = {
  id: string;
  salesProductId: string;
  title: string;
  description?: string | null;
  severity: string;
  status: string;
  correctiveAction?: string | null;
  assignedToUserId?: string | null;
  assignedToUserName?: string | null;
  identifiedAtUtc: string;
  resolvedAtUtc?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type SalesProductIssueUpsertPayload = {
  title: string;
  description?: string | null;
  severity: string;
  status: string;
  correctiveAction?: string | null;
  assignedToUserId?: string | null;
  identifiedAtUtc: string;
  resolvedAtUtc?: string | null;
};

export type SalesProductChangeRequest = {
  id: string;
  salesProductId: string;
  title: string;
  description?: string | null;
  impactScope?: string | null;
  impactSchedule?: string | null;
  impactCost?: string | null;
  status: string;
  requestedByUserId: string;
  requestedByUserName?: string | null;
  reviewedByUserId?: string | null;
  reviewedByUserName?: string | null;
  submittedAtUtc?: string | null;
  reviewedAtUtc?: string | null;
  reviewNotes?: string | null;
  rejectionReason?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  azureLinks: SalesProductAzureLink[];
};

export type SalesProductChangeRequestUpsertPayload = {
  title: string;
  description?: string | null;
  impactScope?: string | null;
  impactSchedule?: string | null;
  impactCost?: string | null;
};

function productPath(productId: string, suffix: string): string {
  return `/v1/sales/products/${productId}/${suffix}`;
}

export async function listSalesProductLinkedApplications(
  productId: string,
): Promise<SalesProductLinkedApplication[]> {
  const response = await apiClient.get<SalesProductLinkedApplication[]>(
    productPath(productId, 'linked-applications'),
  );
  return response.data ?? [];
}

export async function listSalesProductBacklog(productId: string): Promise<SalesProductBacklogItem[]> {
  const response = await apiClient.get<SalesProductBacklogItem[]>(productPath(productId, 'backlog'));
  return response.data ?? [];
}

export async function createSalesProductBacklogItem(
  productId: string,
  payload: SalesProductBacklogItemUpsertPayload,
): Promise<SalesProductBacklogItem> {
  const response = await apiClient.post<SalesProductBacklogItem>(productPath(productId, 'backlog'), payload);
  return response.data;
}

export async function updateSalesProductBacklogItem(
  productId: string,
  itemId: string,
  payload: SalesProductBacklogItemUpsertPayload,
): Promise<SalesProductBacklogItem> {
  const response = await apiClient.put<SalesProductBacklogItem>(
    productPath(productId, `backlog/${itemId}`),
    payload,
  );
  return response.data;
}

export async function deleteSalesProductBacklogItem(productId: string, itemId: string): Promise<void> {
  await apiClient.delete(productPath(productId, `backlog/${itemId}`));
}

export async function linkSalesProductBacklogAzure(
  productId: string,
  itemId: string,
  payload: SalesProductAzureLinkResolvePayload,
): Promise<SalesProductAzureLink> {
  const response = await apiClient.post<SalesProductAzureLink>(
    productPath(productId, `backlog/${itemId}/azure-links`),
    payload,
  );
  return response.data;
}

export async function refreshSalesProductBacklogAzureLink(
  productId: string,
  itemId: string,
  linkId: string,
): Promise<SalesProductAzureLink> {
  const response = await apiClient.post<SalesProductAzureLink>(
    productPath(productId, `backlog/${itemId}/azure-links/${linkId}/refresh`),
  );
  return response.data;
}

export async function deleteSalesProductBacklogAzureLink(
  productId: string,
  itemId: string,
  linkId: string,
): Promise<void> {
  await apiClient.delete(productPath(productId, `backlog/${itemId}/azure-links/${linkId}`));
}

export async function listSalesProductRoadmap(productId: string): Promise<SalesProductRoadmapItem[]> {
  const response = await apiClient.get<SalesProductRoadmapItem[]>(productPath(productId, 'roadmap'));
  return response.data ?? [];
}

export async function createSalesProductRoadmapItem(
  productId: string,
  payload: SalesProductRoadmapItemUpsertPayload,
): Promise<SalesProductRoadmapItem> {
  const response = await apiClient.post<SalesProductRoadmapItem>(productPath(productId, 'roadmap'), payload);
  return response.data;
}

export async function updateSalesProductRoadmapItem(
  productId: string,
  itemId: string,
  payload: SalesProductRoadmapItemUpsertPayload,
): Promise<SalesProductRoadmapItem> {
  const response = await apiClient.put<SalesProductRoadmapItem>(
    productPath(productId, `roadmap/${itemId}`),
    payload,
  );
  return response.data;
}

export async function deleteSalesProductRoadmapItem(productId: string, itemId: string): Promise<void> {
  await apiClient.delete(productPath(productId, `roadmap/${itemId}`));
}

export async function listSalesProductRisks(productId: string): Promise<SalesProductRisk[]> {
  const response = await apiClient.get<SalesProductRisk[]>(productPath(productId, 'risks'));
  return response.data ?? [];
}

export async function createSalesProductRisk(
  productId: string,
  payload: SalesProductRiskUpsertPayload,
): Promise<SalesProductRisk> {
  const response = await apiClient.post<SalesProductRisk>(productPath(productId, 'risks'), payload);
  return response.data;
}

export async function updateSalesProductRisk(
  productId: string,
  itemId: string,
  payload: SalesProductRiskUpsertPayload,
): Promise<SalesProductRisk> {
  const response = await apiClient.put<SalesProductRisk>(productPath(productId, `risks/${itemId}`), payload);
  return response.data;
}

export async function deleteSalesProductRisk(productId: string, itemId: string): Promise<void> {
  await apiClient.delete(productPath(productId, `risks/${itemId}`));
}

export async function listSalesProductIssues(productId: string): Promise<SalesProductIssue[]> {
  const response = await apiClient.get<SalesProductIssue[]>(productPath(productId, 'issues'));
  return response.data ?? [];
}

export async function createSalesProductIssue(
  productId: string,
  payload: SalesProductIssueUpsertPayload,
): Promise<SalesProductIssue> {
  const response = await apiClient.post<SalesProductIssue>(productPath(productId, 'issues'), payload);
  return response.data;
}

export async function updateSalesProductIssue(
  productId: string,
  itemId: string,
  payload: SalesProductIssueUpsertPayload,
): Promise<SalesProductIssue> {
  const response = await apiClient.put<SalesProductIssue>(productPath(productId, `issues/${itemId}`), payload);
  return response.data;
}

export async function deleteSalesProductIssue(productId: string, itemId: string): Promise<void> {
  await apiClient.delete(productPath(productId, `issues/${itemId}`));
}

export async function listSalesProductChanges(productId: string): Promise<SalesProductChangeRequest[]> {
  const response = await apiClient.get<SalesProductChangeRequest[]>(productPath(productId, 'changes'));
  return response.data ?? [];
}

export async function createSalesProductChange(
  productId: string,
  payload: SalesProductChangeRequestUpsertPayload,
): Promise<SalesProductChangeRequest> {
  const response = await apiClient.post<SalesProductChangeRequest>(productPath(productId, 'changes'), payload);
  return response.data;
}

export async function updateSalesProductChange(
  productId: string,
  itemId: string,
  payload: SalesProductChangeRequestUpsertPayload,
): Promise<SalesProductChangeRequest> {
  const response = await apiClient.put<SalesProductChangeRequest>(
    productPath(productId, `changes/${itemId}`),
    payload,
  );
  return response.data;
}

export async function submitSalesProductChange(productId: string, itemId: string): Promise<SalesProductChangeRequest> {
  const response = await apiClient.post<SalesProductChangeRequest>(
    productPath(productId, `changes/${itemId}/submit`),
  );
  return response.data;
}

export async function approveSalesProductChange(productId: string, itemId: string): Promise<SalesProductChangeRequest> {
  const response = await apiClient.post<SalesProductChangeRequest>(
    productPath(productId, `changes/${itemId}/approve`),
  );
  return response.data;
}

export async function rejectSalesProductChange(
  productId: string,
  itemId: string,
  rejectionReason: string,
): Promise<SalesProductChangeRequest> {
  const response = await apiClient.post<SalesProductChangeRequest>(
    productPath(productId, `changes/${itemId}/reject`),
    { rejectionReason },
  );
  return response.data;
}

export async function deleteSalesProductChange(productId: string, itemId: string): Promise<void> {
  await apiClient.delete(productPath(productId, `changes/${itemId}`));
}

export async function linkSalesProductChangeAzure(
  productId: string,
  itemId: string,
  payload: SalesProductAzureLinkResolvePayload,
): Promise<SalesProductAzureLink> {
  const response = await apiClient.post<SalesProductAzureLink>(
    productPath(productId, `changes/${itemId}/azure-links`),
    payload,
  );
  return response.data;
}

export async function refreshSalesProductChangeAzureLink(
  productId: string,
  itemId: string,
  linkId: string,
): Promise<SalesProductAzureLink> {
  const response = await apiClient.post<SalesProductAzureLink>(
    productPath(productId, `changes/${itemId}/azure-links/${linkId}/refresh`),
  );
  return response.data;
}

export async function deleteSalesProductChangeAzureLink(
  productId: string,
  itemId: string,
  linkId: string,
): Promise<void> {
  await apiClient.delete(productPath(productId, `changes/${itemId}/azure-links/${linkId}`));
}

export type SalesProductRequirement = {
  id: string;
  salesProductId: string;
  title: string;
  validationCriteria: string;
  backlogItemCount: number;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type SalesProductRequirementUpsertPayload = {
  title: string;
  validationCriteria: string;
};

export async function listSalesProductRequirements(productId: string): Promise<SalesProductRequirement[]> {
  const response = await apiClient.get<SalesProductRequirement[]>(productPath(productId, 'requirements'));
  return response.data ?? [];
}

export async function createSalesProductRequirement(
  productId: string,
  payload: SalesProductRequirementUpsertPayload,
): Promise<SalesProductRequirement> {
  const response = await apiClient.post<SalesProductRequirement>(productPath(productId, 'requirements'), payload);
  return response.data;
}

export async function updateSalesProductRequirement(
  productId: string,
  requirementId: string,
  payload: SalesProductRequirementUpsertPayload,
): Promise<SalesProductRequirement> {
  const response = await apiClient.put<SalesProductRequirement>(
    productPath(productId, `requirements/${requirementId}`),
    payload,
  );
  return response.data;
}

export async function deleteSalesProductRequirement(productId: string, requirementId: string): Promise<void> {
  await apiClient.delete(productPath(productId, `requirements/${requirementId}`));
}

export async function listSalesProductBacklogRequirements(
  productId: string,
  itemId: string,
): Promise<SalesProductRequirement[]> {
  const response = await apiClient.get<SalesProductRequirement[]>(
    productPath(productId, `backlog/${itemId}/requirements`),
  );
  return response.data ?? [];
}

export async function linkSalesProductBacklogRequirement(
  productId: string,
  itemId: string,
  requirementId: string,
): Promise<SalesProductRequirement> {
  const response = await apiClient.post<SalesProductRequirement>(
    productPath(productId, `backlog/${itemId}/requirements/${requirementId}`),
  );
  return response.data;
}

export async function unlinkSalesProductBacklogRequirement(
  productId: string,
  itemId: string,
  requirementId: string,
): Promise<void> {
  await apiClient.delete(productPath(productId, `backlog/${itemId}/requirements/${requirementId}`));
}
